import { priorities, statuses, types } from "@/components/task/utils";
import { DatePickerRange } from "@/components/ui/date-picker-range";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataService } from "@/services/data";
import { TaskType } from "@/types/user";
import { DndContext, DragOverlay, MouseSensor, closestCenter, useDraggable, useDroppable, useSensor, useSensors } from "@dnd-kit/core";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import { useNavigate, useRevalidator, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { TaskCard } from "./task-card";

export const DataKanban = ({ initialTasks, is_manager = false }: { initialTasks: TaskType[]; is_manager: boolean }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchQuery, setSearchQuery] = useState<string | undefined>();
    const [tasks, setTasks] = useState<TaskType[]>(initialTasks);
    const [activeTask, setActiveTask] = useState<TaskType | null>(null);
    const [overColumnId, setOverColumnId] = useState<string | null>(null);
    const [groupedBy, setGroupedBy] = useState<string>("status");
    const [orderBy, setOrderBy] = useState<string>(searchParams.get("o") ?? "due_date");
    const [groups, setGroups] = useState(statuses);
    const revalidator = useRevalidator();
    const navigate = useNavigate();

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: { distance: 5 }
        })
    );

    useEffect(() => {
        switch (groupedBy) {
            case "status":
                setGroups(statuses);
                break;
            case "type":
                setGroups(types);
                break;
            case "priority":
                setGroups(priorities);
                break;
            default:
                console.error("unknow group", groupedBy);
                break;
        }
    }, [groupedBy]);

    useEffect(() => {
        setTasks(initialTasks);
    }, [initialTasks]);

    return (
        <div className="max-h-[520px]">
            <div className="flex flex-row space-x-2 py-2">
                <Input
                    placeholder={"Search a task..."}
                    value={searchQuery}
                    onChange={event => {
                        setSearchQuery(event.target.value);
                    }}
                    className="max-w-sm"
                />
                <DatePickerRange
                    date={
                        searchParams.get(`due_date__range`)
                            ? {
                                  from: DateTime.fromISO(searchParams.get(`due_date__range`)?.split(",")[0] as string).toJSDate(),
                                  to: DateTime.fromISO(searchParams.get(`due_date__range`)?.split(",")[1] as string).toJSDate()
                              }
                            : undefined
                    }
                    setDate={(date?: DateRange) => {
                        if (date) {
                            let _searchParams = new URLSearchParams(searchParams);
                            if (date.from && date.to) {
                                _searchParams.set(`due_date__range`, `${date.from.toISOString().split("T")[0]},${date.to.toISOString().split("T")[0]}`);
                            } else {
                                _searchParams.delete(`due_date__range`);
                            }
                            setSearchParams(_searchParams);
                        }
                    }}
                />
                <Select
                    value={groupedBy}
                    onValueChange={value => {
                        setGroupedBy(value);
                    }}
                >
                    <SelectTrigger className="w-[220px]">
                        <SelectValue placeholder="Group by" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectGroup>
                            <SelectLabel>Group by</SelectLabel>
                            <SelectItem value="priority">Priority</SelectItem>
                            <SelectItem value="status">Status</SelectItem>
                            <SelectItem value="type">Type</SelectItem>
                        </SelectGroup>
                    </SelectContent>
                </Select>
                <Select
                    value={orderBy}
                    onValueChange={value => {
                        setOrderBy(value);
                        searchParams.set("o", value);
                        setSearchParams(searchParams);
                    }}
                >
                    <SelectTrigger className="w-[220px]">
                        <SelectValue placeholder="Ordonner par" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectGroup>
                            <SelectLabel>Order by</SelectLabel>
                            <SelectItem value="priority">Priority</SelectItem>
                            <SelectItem value="status">Status</SelectItem>
                            <SelectItem value="type">Type</SelectItem>
                            <SelectItem value="due_date">Due date</SelectItem>
                        </SelectGroup>
                    </SelectContent>
                </Select>
            </div>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={async event => {
                    const { active, over } = event;
                    if (!over || active.id === over.id) return;

                    const task = tasks.find(t => t.id.toString() === active.id);
                    if (!task) return;

                    if (task[`${groupedBy}_editable` as keyof typeof activeTask] === false) {
                        setActiveTask(null);
                        setOverColumnId(null);
                        return;
                    }

                    const newStatus = over.id.toString();

                    const updatedTasks = tasks.map(t => (t.id === task.id ? { ...t, [groupedBy]: newStatus } : t));

                    // optimistic update
                    setTasks(updatedTasks as TaskType[]);

                    if (task[groupedBy as keyof typeof task] !== newStatus) {
                        await DataService.client
                            .url(`/users/tasks/${task.id}/`)
                            .patch({ [groupedBy]: newStatus })
                            .json()
                            .then(() => {
                                revalidator.revalidate();
                            })
                            .catch(() => {
                                toast.error("Une erreur est survenue");
                                // revert optimistic update if error
                                setTasks(tasks);
                            });
                    }
                    setActiveTask(null);
                    setOverColumnId(null);
                }}
                onDragStart={event => {
                    setOverColumnId(null);
                    const task = tasks.find(t => t.id.toString() === event.active.id);
                    setActiveTask(task || null);
                }}
                onDragOver={event => {
                    const overId = event.over?.id?.toString();
                    const isOverGroup = groups.some(group => group.value === overId);
                    setOverColumnId(isOverGroup && overId ? overId : null);
                }}
                onDragCancel={() => {
                    setOverColumnId(null);
                    setActiveTask(null);
                }}
            >
                <div className={`flex w-full flex-row overflow-hidden`}>
                    {groups.map((group, i) => (
                        <Column
                            key={i}
                            status={group}
                            groupedBy={groupedBy}
                            isOver={overColumnId === group.value}
                            tasks={tasks.filter(task => task[groupedBy as keyof typeof task] === group.value)}
                            draggedId={activeTask?.id?.toString() ?? null}
                            navigate={navigate}
                            revalidator={revalidator}
                            searchQuery={searchQuery}
                            groups={groups}
                            activeTask={activeTask}
                            is_manager={is_manager}
                        />
                    ))}
                </div>

                <DragOverlay>{activeTask && <DraggableCard is_manager={is_manager} task={activeTask} navigate={navigate} revalidator={revalidator} isOverlay={true} />}</DragOverlay>
            </DndContext>
        </div>
    );
};

function Column({ status, tasks, draggedId, navigate, revalidator, isOver, searchQuery, groups, groupedBy, activeTask, is_manager }: any) {
    const { setNodeRef } = useDroppable({ id: status.value });
    const disabled = activeTask ? activeTask[`${groupedBy}_editable` as keyof typeof activeTask] === false : false;
    return (
        <div className="relative flex h-full flex-col" style={{ width: `${100 / groups.length}%` }}>
            <div className="flex flex-row items-center space-x-1 p-2">
                <h1>{status.icon}</h1>
                <span>{status.label}</span>
                <span>({tasks.length})</span>
            </div>
            {isOver && (
                <div
                    className={`${disabled ? "text-red-500" : "text-black"} absolute inset-0 flex min-h-[480px] items-center justify-center border-[1px] bg-gray-200 text-center text-sm italic opacity-85`}
                    style={{ zIndex: 999 }}
                >
                    {disabled ? "You are not authorized to move this task" : "Drop here to move the task."}
                </div>
            )}
            <div ref={setNodeRef} className={`max-h-[440px] min-h-full space-y-1 overflow-scroll px-2 transition-colors duration-200`}>
                {(tasks as TaskType[])
                    .filter(task => {
                        if (searchQuery) {
                            return task.title.toLowerCase().includes(searchQuery.toLowerCase()) || task?.description?.toLowerCase().includes(searchQuery.toLowerCase());
                        }
                        return true;
                    })
                    .map((task: TaskType) => (
                        <DraggableCard
                            is_manager={is_manager}
                            disabled={task[`${groupedBy}_editable` as keyof typeof task] === false}
                            key={task.id}
                            task={task}
                            dragged={draggedId === task.id.toString()}
                            navigate={navigate}
                            revalidator={revalidator}
                        />
                    ))}
            </div>
        </div>
    );
}

function DraggableCard({ task, is_manager, isOverlay = false }: any) {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: task.id.toString()
    });

    const style = {
        transform: isDragging ? `translate(${transform?.x}px, ${transform?.y}px)` : undefined,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isOverlay ? 9999 : "auto",
        boxShadow: isOverlay ? "0px 5px 15px rgba(0,0,0,0.5)" : undefined
    };

    return (
        <TaskCard
            task={task}
            is_manager={is_manager}
            ref={isOverlay ? undefined : setNodeRef}
            style={style}
            className="cursor-pointer"
            {...listeners}
            {...(isOverlay ? {} : { ...listeners, ...attributes })}
        />
    );
}
